/**
 * docxTextPatch.ts
 * Writes text edits from the rendered template preview DOM back into
 * word/document.xml so renamed/inserted `{{placeholder}}` tokens (and label
 * text) become part of the real DOCX used by fill / preview / export.
 *
 * Matching (in order):
 * 1. Exact plain-text match on <w:p> (nbsp-normalized)
 * 2. Collapsed-whitespace match (docx-preview often differs on spaces)
 * 3. Same body-paragraph index (article <p> ↔ <w:p>)
 * 4. Rename {{tokens}} in-document when from→to only changes placeholders
 *
 * Preview paragraphs are taken from `section.docx article` so header/footer
 * DOM nodes do not steal body indices.
 */

import PizZip from "pizzip";
import { repairSplitMarkers } from "@/utils/docxMarker";

const INITIAL_TEXT_ATTR = "data-initial-text";
const EDITED_TEXT_ATTR = "data-user-edited-text";

export interface ParagraphTextPatch {
  /** Plain text of the paragraph before the user edited it. */
  from: string;
  /** Plain text after the edit (may include {{placeholders}}). */
  to: string;
  /** Index among body preview paragraphs (`article p`), when known. */
  index?: number;
}

export interface ApplyTextPatchesResult {
  xml: string;
  patchedCount: number;
  unmatched: ParagraphTextPatch[];
}

export interface ApplyPreviewTextEditsResult {
  fileData: Uint8Array;
  patchedCount: number;
  unmatched: ParagraphTextPatch[];
}

function escapeXmlText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function unescapeXmlText(text: string): string {
  return text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/** Concatenate every <w:t> in a paragraph, decoding XML entities. */
export function paragraphPlainText(paraXml: string): string {
  const matches = paraXml.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/g) ?? [];
  return matches
    .map((t) =>
      unescapeXmlText(t.replace(/<w:t[^>]*>/, "").replace(/<\/w:t>$/, ""))
    )
    .join("");
}

function normalizePlain(text: string): string {
  return text.replace(/\u00A0/g, " ");
}

/** Collapse runs of whitespace — bridges DOM vs Word spacing differences. */
function collapseWs(text: string): string {
  return normalizePlain(text).replace(/\s+/g, " ").trim();
}

function listPlaceholders(text: string): string[] {
  return text.match(/\{\{[^{}]*\}\}/g) ?? [];
}

/** Write `plain` into the first <w:t> of a paragraph; clear the rest. */
export function writeParagraphPlainText(paraXml: string, plain: string): string {
  const escaped = escapeXmlText(plain);
  if (!/<w:t[\s>]/i.test(paraXml)) {
    return paraXml.replace(
      /<\/w:p>/i,
      `<w:r><w:t xml:space="preserve">${escaped}</w:t></w:r></w:p>`
    );
  }
  let first = true;
  return paraXml.replace(/<w:t(\s[^>]*)?>[\s\S]*?<\/w:t>/gi, (_tTag, attrs = "") => {
    if (!first) return `<w:t${attrs}></w:t>`;
    first = false;
    const hasSpace = /\bxml:space=/.test(attrs);
    const attr = hasSpace ? attrs : `${attrs} xml:space="preserve"`;
    return `<w:t${attr}>${escaped}</w:t>`;
  });
}

/** Body paragraphs only — skip header/footer preview chrome. */
export function previewBodyParagraphs(container: HTMLElement): HTMLElement[] {
  if (!container) return [];
  const articles = container.querySelectorAll("section.docx article");
  if (articles.length > 0) {
    return Array.from(articles).flatMap((article) =>
      Array.from(article.querySelectorAll<HTMLElement>("p"))
    );
  }
  return Array.from(container.querySelectorAll<HTMLElement>("section.docx p, p"));
}

/**
 * Snapshot every body paragraph's current text so later patches can find the
 * matching <w:p> even after the DOM text has changed.
 */
export function snapshotParagraphInitialText(container: HTMLElement): void {
  if (!container) return;
  previewBodyParagraphs(container).forEach((p) => {
    if (!p.hasAttribute(INITIAL_TEXT_ATTR)) {
      p.setAttribute(INITIAL_TEXT_ATTR, p.textContent ?? "");
    }
  });
}

/**
 * Collect from→to patches for paragraphs the user edited in the preview body.
 */
export function collectParagraphTextPatches(container: HTMLElement): ParagraphTextPatch[] {
  if (!container) return [];
  const patches: ParagraphTextPatch[] = [];
  previewBodyParagraphs(container).forEach((p, index) => {
    const edited =
      p.hasAttribute(EDITED_TEXT_ATTR) || !!p.querySelector(`[${EDITED_TEXT_ATTR}]`);
    if (!edited) return;
    // Require a snapshot — without it we cannot safely match <w:p>.
    if (!p.hasAttribute(INITIAL_TEXT_ATTR)) return;
    const from = p.getAttribute(INITIAL_TEXT_ATTR) ?? "";
    const to = p.textContent ?? "";
    if (normalizePlain(from) === normalizePlain(to)) return;
    patches.push({ from, to, index });
  });
  return patches;
}

function placeholderRenames(
  from: string,
  to: string
): Array<{ oldToken: string; newToken: string }> {
  const fromPh = listPlaceholders(from);
  const toPh = listPlaceholders(to);
  if (fromPh.length === 0 || fromPh.length !== toPh.length) return [];
  const out: Array<{ oldToken: string; newToken: string }> = [];
  for (let i = 0; i < fromPh.length; i++) {
    if (fromPh[i] !== toPh[i]) {
      out.push({ oldToken: fromPh[i], newToken: toPh[i] });
    }
  }
  return out;
}

/** Tokens present in `from` but absent in `to` (user deleted / cleared them). */
function placeholderRemovals(from: string, to: string): string[] {
  const toPh = new Set(listPlaceholders(to));
  return listPlaceholders(from).filter((t) => !toPh.has(t));
}

function applyPlaceholderRenames(
  docXml: string,
  renames: Array<{ oldToken: string; newToken: string }>
): { xml: string; count: number } {
  if (renames.length === 0) return { xml: docXml, count: 0 };
  // Ensure tokens are contiguous before string replace.
  let xml = repairSplitMarkers(docXml).xml;
  let count = 0;
  for (const { oldToken, newToken } of renames) {
    if (!oldToken || oldToken === newToken) continue;
    if (!xml.includes(oldToken)) continue;
    xml = xml.split(oldToken).join(newToken);
    count += 1;
  }
  return { xml, count };
}

/**
 * Find the first <w:p> that still contains any of `tokens` and replace its
 * plain text with `to` (handles delete/clear when DOM index ≠ XML index).
 */
function applyParagraphByTokens(
  docXml: string,
  tokens: string[],
  to: string
): { xml: string; patched: boolean } {
  if (tokens.length === 0) return { xml: docXml, patched: false };
  let xml = repairSplitMarkers(docXml).xml;
  let patched = false;
  xml = xml.replace(/<w:p\b[\s\S]*?<\/w:p>/gi, (paraXml) => {
    if (patched) return paraXml;
    const plain = paragraphPlainText(paraXml);
    if (!tokens.some((t) => plain.includes(t))) return paraXml;
    patched = true;
    return writeParagraphPlainText(paraXml, to);
  });
  return { xml, patched };
}

/**
 * Apply paragraph plain-text replacements to document.xml.
 */
export function applyParagraphTextPatchesToXml(
  docXml: string,
  patches: ParagraphTextPatch[]
): ApplyTextPatchesResult {
  if (!patches.length) return { xml: docXml, patchedCount: 0, unmatched: [] };

  const remaining = patches.map((p) => ({ ...p }));
  let patchedCount = 0;

  const paras = docXml.match(/<w:p\b[\s\S]*?<\/w:p>/gi) ?? [];

  const replaced = paras.map((paraXml, paraIndex) => {
    const plain = paragraphPlainText(paraXml);
    const exact = normalizePlain(plain);
    const collapsed = collapseWs(plain);

    let idx = remaining.findIndex((p) => normalizePlain(p.from) === exact);
    if (idx < 0) {
      idx = remaining.findIndex((p) => collapseWs(p.from) === collapsed);
    }
    if (idx < 0) {
      // Index fallback: same body slot, and placeholders still look related
      // (handles w:tab / spacing the DOM cannot mirror exactly).
      idx = remaining.findIndex((p) => {
        if (p.index !== paraIndex) return false;
        const fromPh = listPlaceholders(p.from);
        const xmlPh = listPlaceholders(plain);
        if (fromPh.length === 0 && xmlPh.length === 0) return true;
        return fromPh.some((t) => xmlPh.includes(t));
      });
    }

    if (idx < 0) return paraXml;
    const { to } = remaining[idx];
    remaining.splice(idx, 1);
    patchedCount += 1;
    return writeParagraphPlainText(paraXml, to);
  });

  // Rebuild XML with replaced paragraphs (same order / count).
  let paraCursor = 0;
  let xml = docXml.replace(/<w:p\b[\s\S]*?<\/w:p>/gi, () => replaced[paraCursor++] ?? "");

  // Fallback: rename or remove {{tokens}} for patches that still did not land.
  const stillUnmatched: ParagraphTextPatch[] = [];
  for (const patch of remaining) {
    const renames = placeholderRenames(patch.from, patch.to);
    if (renames.length > 0) {
      const renamed = applyPlaceholderRenames(xml, renames);
      if (renamed.count > 0) {
        xml = renamed.xml;
        patchedCount += renamed.count;
        const allOldGone = renames.every(({ oldToken }) => !xml.includes(oldToken));
        if (allOldGone) continue;
      }
    }

    const removals = placeholderRemovals(patch.from, patch.to);
    if (removals.length > 0) {
      const byToken = applyParagraphByTokens(xml, removals, patch.to);
      if (byToken.patched) {
        xml = byToken.xml;
        patchedCount += 1;
        continue;
      }
    }

    stillUnmatched.push(patch);
  }

  return { xml, patchedCount, unmatched: stillUnmatched };
}

/**
 * Read text edits from the preview DOM and write them into the DOCX buffer.
 */
export function applyPreviewTextEditsToDocx(
  fileData: ArrayBuffer | Uint8Array,
  container: HTMLElement
): ApplyPreviewTextEditsResult {
  const patches = collectParagraphTextPatches(container);
  if (patches.length === 0) {
    const raw = fileData instanceof Uint8Array ? fileData : new Uint8Array(fileData);
    return { fileData: raw, patchedCount: 0, unmatched: [] };
  }

  const zip = new PizZip(fileData);
  let docXml = zip.files["word/document.xml"]?.asText() ?? "";
  // Merge split {{…}} runs first so token fallback and field parse work.
  docXml = repairSplitMarkers(docXml).xml;

  const { xml, patchedCount, unmatched } = applyParagraphTextPatchesToXml(docXml, patches);
  if (patchedCount > 0 || xml !== docXml) {
    zip.file("word/document.xml", xml);
  }

  const next = zip.generate({
    type: "uint8array",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  }) as Uint8Array;

  return { fileData: next, patchedCount, unmatched };
}
